/**
 * View model for the **prise** step (S1a / S2a): what the hand is asked to
 * do, and what it is currently doing.
 *
 * The FSM owns *which* shape is current (`positionIndex`); the shapes and
 * their places come from `lib/learning-journey/hand-shapes.ts`. What is left
 * — and lives here — is the reading of the piano against that shape, plus
 * the sentences that name what is happening. Both are pure, so the component
 * stays a renderer.
 *
 * ## The player executes a shape once, at its first place
 *
 * A shape can recur: four fifths fingered 5-1 at four pitches are one thing
 * to learn, not four. So the step asks for the configuration once, at the
 * first place the phrase uses it, and the other places are shown rather than
 * replayed — the moving between them is what S1b is for, thirty seconds
 * later.
 *
 * ## Validation rule
 *
 * Success is **the set of keys currently held equals the place**, at any
 * instant. No time window: a beginner takes a chord deliberately, and
 * punishing that would teach hurry. Simultaneity is already imposed by
 * having to hold the keys together, which is the physical fact we want.
 * Timers exist only to decide *when to speak*, never to decide success.
 *
 * ## Wrong octave is not "wrong keys"
 *
 * The same shape an octave off is the single most common beginner miss on a
 * piano-roll, and telling that player "the D is not in the grip" is useless.
 * It is detected before the extra-key reading and gets its own sentence.
 *
 * ## Two acts: find, then complete — and sometimes only one
 *
 * The step opens on what the roll drops **first** in this place, named but
 * not lit. Finding it among 88 keys is a retrieval, and retrieval is what
 * the note-by-note step never asked for: it showed the name and lit the key,
 * so there was nothing to recall.
 *
 * The opening keys and not the region's low end. An earlier version named
 * the lowest key of the region; on the left hand of *Libérée délivrée* the
 * panel therefore said "ta main part de La#2" while the roll dropped a D3
 * first. Any divergence between the panel and the roll is read as a bug in
 * the app, and here it was one.
 *
 * When the place opens on its whole shape — a plaqué chord — there is
 * nothing left to spread onto, and the step is a single act: the copy names
 * every key with its finger and asks for them together.
 *
 * ## Finger numbers, which are safe here
 *
 * Inside a grip the assignment is constant by construction, so a still panel
 * can state it without freezing a hand that reconfigures, and it cannot
 * contradict the roll since it is read off the roll. They are written as
 * digits, the way they appear on a score: `Ré3 (5)`. A key taken by two
 * fingers — a substitution on a repeated note — is written `Sol2 (3-5)`
 * rather than picking a winner.
 */

import {
  namedGaps,
  shapeDirection,
  type HandShape,
  type WideGap,
} from '@/lib/learning-journey/hand-shapes';
import { Note } from '@/lib/music/note';
import type {
  GripFinger,
  PhraseGrip,
  PhraseNote,
} from '@/lib/learning-journey/types';

// ── Labellers ─────────────────────────────────────────────────────

/**
 * The two labellers the copy needs, built from the notation preference.
 *
 * `key` names a key — `Ré3` — and `pitchClass` names a note without saying
 * where it is — `Ré`. Both spellings have to come from the same preference,
 * so they are built together rather than assembled at each call site.
 */
/**
 * A key's name split from where it is — `Si4` → `{ name: 'Si', octave: 4 }`.
 *
 * Because a number stuck to a note name means **fingering** everywhere else
 * in this product: the roll paints 1-to-5 badges on every note, and this very
 * step tells a screen reader « Si4 doigt 3 ». The octaves a hand actually
 * works span 3 to 6, so 3, 4 and 5 read as either. Set apart, they cannot.
 *
 * Returns `null` for a spelling with no octave (`pitchClass` labels), which
 * is the caller's signal that there is nothing to set apart.
 */
export function splitOctave(
  label: string,
): { readonly name: string; readonly octave: string } | null {
  const match = /^(.*?)(-?\d+)$/.exec(label);

  return match === null
    ? null
    : { name: match[1], octave: match[2] };
}

export function noteLabellers(toNoteLabel: (name: string) => string): {
  readonly key: (midi: number) => string;
  readonly pitchClass: (pitchClass: number) => string;
} {
  return {
    key: (midi) => toNoteLabel(Note.fromMidi(midi).toName()),
    // `noteLabel` takes the octave as optional, so stripping it before
    // spelling is enough — the octave never reaches the notation table.
    pitchClass: (pc) =>
      toNoteLabel(Note.fromMidi(60 + pc).toName().replace(/-?\d+$/, '')),
  };
}

// ── The place the step takes the player to ────────────────────────

/**
 * The keys struck at the place's first onset — what the roll drops first.
 *
 * A lookup in notes the journey already holds, not a second segmentation:
 * the analyzer decided where the grip starts, and this reads which of its
 * keys are struck there. Falls back to the whole place when no note lands
 * inside it, which makes the step a single act rather than a hunt for a key
 * the roll never shows.
 */
export function openingKeys(
  place: PhraseGrip,
  notes: readonly PhraseNote[],
): number[] {
  let firstTick: number | null = null;

  for (const note of notes) {
    if (note.startTick < place.startTick || note.startTick >= place.endTick) {
      continue;
    }

    if (!place.midis.includes(note.midi)) {
      continue;
    }

    if (firstTick === null || note.startTick < firstTick) {
      firstTick = note.startTick;
    }
  }

  if (firstTick === null) {
    return [...place.midis];
  }

  const opening = notes
    .filter((n) => n.startTick === firstTick && place.midis.includes(n.midi))
    .map((n) => n.midi);

  return [...new Set(opening)].sort((a, b) => a - b);
}

/**
 * Whether the place opens on its whole shape, making the step one act.
 *
 * A containment and not a length comparison. `openingKeys` returns a
 * deduplicated subset of the place, so on that input the two agree — but
 * this is exported, and a caller passing an opening read off anything else
 * would get `true` for a set of the right size and the wrong keys, which
 * would silently collapse the two acts into one.
 */
export function opensWhole(
  place: PhraseGrip,
  opening: readonly number[],
): boolean {
  return place.midis.every((midi) => opening.includes(midi));
}

// ── Evaluation ────────────────────────────────────────────────────

export type GripPhase =
  /** Nothing of the shape is down yet. */
  | 'idle'
  /** The opening keys are not down yet, and the rest is still hidden. */
  | 'seeking'
  /** Some of it is down, nothing wrong. */
  | 'partial'
  /** The right shape, transposed by whole octaves. */
  | 'wrong-octave'
  /** A key that is not in the shape is down. */
  | 'extra'
  /** Exactly the shape, nothing else. */
  | 'complete';

export type GripEvaluation = {
  readonly phase: GripPhase;
  /** Keys of the target currently held. */
  readonly pressed: readonly number[];
  /** Keys of the target not held yet. */
  readonly missing: readonly number[];
  /** Held keys that are not in the target. */
  readonly extra: readonly number[];
  /** Semitone offset of the shape when `phase === 'wrong-octave'`. */
  readonly octaveOffset: number | null;
};

/**
 * Reading of the piano before the shape is revealed: the player is looking
 * for what the roll drops first, and every other key is simply not it.
 */
export function evaluateOpening(
  opening: readonly number[],
  active: ReadonlySet<number>,
): GripEvaluation {
  const pressed = opening.filter((m) => active.has(m));
  const missing = opening.filter((m) => !active.has(m));
  const extra = [...active]
    .filter((m) => !opening.includes(m))
    .sort((a, b) => a - b);

  if (missing.length === 0) {
    return { phase: 'complete', pressed, missing, extra, octaveOffset: null };
  }

  return { phase: 'seeking', pressed, missing, extra, octaveOffset: null };
}

/**
 * Reading of the shape when the piece does not strike it whole.
 *
 * ## The keys are taken one at a time, and nothing has to stay down
 *
 * The other reading — `evaluateGrip` — asks for every key of the shape down
 * at the same instant. Measured on the catalogue, that asks for a chord the
 * piece never plays in **82.6%** of shapes, and on a five-key position it is
 * a cluster: the step opens on a dissonance and congratulates it.
 *
 * So each key of the shape is struck at some point, in any order, once the
 * anchor has been found. It used to have to be struck *while the anchor was
 * still held*, which certified the span; it also turned the step into a
 * contortion the player kept failing for a reason that had nothing to do
 * with finding the keys, and the panel spent its headline on « keep that
 * finger down ». The keyboard shows the span; the step now asks for the keys.
 *
 * `covered` is what the caller has accumulated, since a key struck and
 * released has been taken all the same. The anchor counts as taken: finding
 * it was act 1. Keys outside the shape are reported and never block: this
 * step is not a test, and the other hand holding a note is not a mistake.
 */
export function evaluatePlaceWalk(
  place: PhraseGrip,
  anchor: readonly number[],
  active: ReadonlySet<number>,
  covered: ReadonlySet<number>,
): GripEvaluation {
  const pressed = place.midis.filter((m) => covered.has(m) || active.has(m));
  const missing = place.midis.filter(
    (m) => !covered.has(m) && !anchor.includes(m),
  );
  const extra = [...active]
    .filter((m) => !place.midis.includes(m))
    .sort((a, b) => a - b);

  if (missing.length === 0) {
    return { phase: 'complete', pressed, missing, extra, octaveOffset: null };
  }

  return { phase: 'partial', pressed, missing, extra, octaveOffset: null };
}

/**
 * Keys of the shape taken, given what was already taken and what is down now.
 *
 * Returns `covered` unchanged when nothing new was taken, so a caller can
 * compare by reference.
 */
export function coverPlace(
  place: PhraseGrip,
  active: ReadonlySet<number>,
  covered: ReadonlySet<number>,
): ReadonlySet<number> {
  const taken = place.midis.filter((m) => active.has(m) && !covered.has(m));

  if (taken.length === 0) {
    return covered;
  }

  return new Set([...covered, ...taken]);
}

export function evaluateGrip(
  place: PhraseGrip,
  active: ReadonlySet<number>,
): GripEvaluation {
  const pressed = place.midis.filter((m) => active.has(m));
  const missing = place.midis.filter((m) => !active.has(m));
  const extra = [...active]
    .filter((m) => !place.midis.includes(m))
    .sort((a, b) => a - b);

  if (missing.length === 0 && extra.length === 0) {
    return { phase: 'complete', pressed, missing, extra, octaveOffset: null };
  }

  const octaveOffset = detectOctaveShift(place.midis, active);

  if (octaveOffset !== null) {
    return { phase: 'wrong-octave', pressed, missing, extra, octaveOffset };
  }

  if (extra.length > 0) {
    return { phase: 'extra', pressed, missing, extra, octaveOffset: null };
  }

  return {
    phase: pressed.length === 0 ? 'idle' : 'partial',
    pressed,
    missing,
    extra,
    octaveOffset: null,
  };
}

/**
 * Semitone offset when `active` is exactly the shape transposed by whole
 * octaves, `null` otherwise. Only ±1 and ±2 octaves are considered: past
 * that the player is not "a bit off", they are somewhere else.
 */
function detectOctaveShift(
  midis: readonly number[],
  active: ReadonlySet<number>,
): number | null {
  if (active.size !== midis.length) {
    return null;
  }

  for (const offset of [-24, -12, 12, 24]) {
    if (midis.every((m) => active.has(m + offset))) {
      return offset;
    }
  }

  return null;
}

// ── Fingering ─────────────────────────────────────────────────────

/**
 * Fingers on one key of a place, ascending, or an empty list when the
 * analysis carried none.
 */
export function fingersOn(place: PhraseGrip, midi: number): number[] {
  return place.fingers
    .filter((f) => f.midi === midi)
    .map((f) => f.finger)
    .sort((a, b) => a - b);
}

/**
 * How a fingering is written on a key: `5`, or `3-5` for a substitution.
 *
 * Digits and not names, the way a score writes them. Empty when the analysis
 * carried no fingering, and callers drop the parenthesis rather than print
 * an empty one.
 */
export function fingerMark(fingers: readonly number[]): string {
  return fingers.join('-');
}

/**
 * A finger said the way a teacher says it, or `null`.
 *
 * Names and not digits, and only here: on a key the number is right — that
 * is how a score writes it, and it sits on the key it belongs to. In a
 * sentence it is not. « Pose le doigt 1 sur Do4 » asks the reader to
 * translate before they can move, and the panel is already spending numbers
 * on octaves.
 */
export function fingerName(finger: number): string | null {
  switch (finger) {
    case 1:
      return 'le pouce';
    case 2:
      return "l'index";
    case 3:
      return 'le majeur';
    case 4:
      return "l'annulaire";
    case 5:
      return "l'auriculaire";
    default:
      return null;
  }
}

/** The name of the finger taking `midi`, or `ton doigt` when unknown. */
export function fingerWordOn(
  place: PhraseGrip,
  midis: readonly number[],
): string {
  if (midis.length !== 1) {
    return 'tes doigts';
  }

  const fingers = fingersOn(place, midis[0]);
  const named = fingers.length === 1 ? fingerName(fingers[0]) : null;

  return named ?? 'ton doigt';
}

/** `Ré3 (5)`, or just `Ré3` when the fingering is unknown. */
export function keyWithFinger(
  place: PhraseGrip,
  midi: number,
  noteLabel: (midi: number) => string,
): string {
  const mark = fingerMark(fingersOn(place, midi));

  if (mark === '') {
    return noteLabel(midi);
  }

  return `${noteLabel(midi)} (${mark})`;
}

// ── Panel geometry ────────────────────────────────────────────────

/**
 * How large the step's keyboard may be drawn, given the window it must show
 * and the room the panel has.
 *
 * A keyboard drawing keeps the aspect ratio of its window, so height and
 * width are one number, not two. Sizing it from the height alone — which is
 * what this replaced — makes the width whatever it happens to be, and the
 * panel clips the excess: a four-octave window at 96 px tall is 672 px wide
 * against 504 available, so a third of the keyboard was cut off and the keys
 * that fell in it vanished while the copy named them with their fingers.
 *
 * Pure, and here rather than in the component, so the bound is testable
 * without mounting anything.
 */
export function gripKeyboardSize(
  viewBoxWidth: number,
  viewBoxHeight: number,
  maxHeightPx: number,
  maxWidthPx: number,
): { readonly widthPx: number; readonly heightPx: number } {
  const heightPx = Math.min(
    maxHeightPx,
    (maxWidthPx * viewBoxHeight) / viewBoxWidth,
  );

  return { heightPx, widthPx: (heightPx * viewBoxWidth) / viewBoxHeight };
}

// ── Naming the shape ──────────────────────────────────────────────

/**
 * What the hand is holding, named by its **intervals**.
 *
 * A property of the shape and therefore true at every place it is taken:
 * the four fifths of *Libérée délivrée* are a fifth on D, on B♭, on C and on
 * G. The harmony is not — those same four places are Dm, B♭, C and Gm — so
 * naming a chord here would be a per-place claim dressed as a property of
 * the thing being learnt, and it is S0's subject besides.
 *
 * Read off the semitone offsets, nothing else. That keeps it a statement
 * about the hand: "une quinte" says the two keys are seven semitones apart,
 * which the player can see, and claims nothing about what the piece is doing
 * with them. On the left hand of the case this was built against, the shape
 * is a bare fifth every bar while the third sits in the right hand in three
 * bars out of four — a panel that said "sans la tierce" would be describing
 * the left hand and sounding like it described the music.
 *
 * `null` past what can be named honestly. A four-key shape has no short
 * name that is not an interpretation, and silence beats a label the player
 * would have to unlearn.
 */
export function shapeName(shape: HandShape): string | null {
  return structureName(shape.keys.map((k) => k.offset));
}

/** The name a run of semitone offsets carries, without a root. */
function structureName(offsets: readonly number[]): string | null {
  if (offsets.length === 2) {
    return INTERVAL_NAME[offsets[1]] ?? null;
  }

  const triad = TRIAD[offsets.join(',')];

  if (triad === undefined) {
    return null;
  }

  return `un accord ${triad.quality}${INVERSION_SUFFIX[triad.inversion]}`;
}

/** Semitones above the lower key. Compound intervals are not shape names. */
const INTERVAL_NAME: Record<number, string> = {
  1: 'une seconde mineure',
  2: 'une seconde majeure',
  3: 'une tierce mineure',
  4: 'une tierce majeure',
  5: 'une quarte',
  6: 'un triton',
  7: 'une quinte',
  8: 'une sixte mineure',
  9: 'une sixte majeure',
  10: 'une septième mineure',
  11: 'une septième majeure',
  12: 'une octave',
};

/**
 * Three-key shapes, keyed on their offsets from the lowest key.
 *
 * Inversions are named because they are different *shapes* under the hand —
 * a major triad in root position and its first inversion are not the same
 * gesture — which is exactly the distinction this step exists to teach.
 *
 * `rootOffset` is where the root sits above the **bass**, which is what lets
 * a place be named with its root without a chord-detection pass: the table
 * already knows the structure, so rotating the pitch classes again would be
 * a second implementation of one identity, free to disagree with this one.
 * Read it off the stacking — a minor triad in first inversion is bass, bass
 * +4, bass +9, and the root is that last one.
 */
const TRIAD: Record<
  string,
  { readonly quality: string; readonly inversion: 0 | 1 | 2; readonly rootOffset: number }
> = {
  '0,4,7': { quality: 'majeur', inversion: 0, rootOffset: 0 },
  '0,3,7': { quality: 'mineur', inversion: 0, rootOffset: 0 },
  '0,3,6': { quality: 'diminué', inversion: 0, rootOffset: 0 },
  '0,4,8': { quality: 'augmenté', inversion: 0, rootOffset: 0 },
  '0,3,8': { quality: 'majeur', inversion: 1, rootOffset: 8 },
  '0,4,9': { quality: 'mineur', inversion: 1, rootOffset: 9 },
  '0,5,9': { quality: 'majeur', inversion: 2, rootOffset: 5 },
  '0,5,8': { quality: 'mineur', inversion: 2, rootOffset: 5 },
};

const INVERSION_SUFFIX = [
  '',
  ', premier renversement',
  ', deuxième renversement',
] as const;

// ── Naming a place ────────────────────────────────────────────────

/**
 * What the keys of **this place** spell, root included, or `null`.
 *
 * Distinct from `shapeName` on purpose, and the distinction is the whole
 * reason a root can be said here at all. A shape recurs — the four fifths of
 * *Libérée délivrée* are one shape at four pitches — so a root attached to
 * the shape would be false at three places out of four. A place is a fixed
 * set of keys, and `Ré-Fa-La` spells a D minor triad as a matter of
 * arithmetic.
 *
 * It is a claim about the keys under the hand, never about the harmony of
 * the passage: the other hand may be adding a note that makes the bar
 * something else, which is S0's subject. Same reason the name is built from
 * the same table `shapeName` reads — the two can disagree about nothing.
 */
export function placeName(
  place: PhraseGrip,
  pitchClassLabel: (pitchClass: number) => string,
  announcedRoot: number | null,
): string | null {
  const [bass] = place.midis;

  if (bass === undefined) {
    return null;
  }

  const offsets = place.midis.map((m) => m - bass);
  const triad = TRIAD[offsets.join(',')];

  if (triad === undefined) {
    return structureName(offsets);
  }

  const root = (((bass + triad.rootOffset) % 12) + 12) % 12;

  if (announcedRoot !== root) {
    return structureName(offsets);
  }

  return `un accord de ${pitchClassLabel(root)} ${triad.quality}${INVERSION_SUFFIX[triad.inversion]}`;
}

/**
 * The root the analyzer states at `tick`, or `null` where it states nothing.
 *
 * A grip is an occupation *over a span*, so the keys it holds can stack in
 * thirds without any of it being harmony: a hand walking an arpeggio through
 * a chord change spells triads that exist nowhere in the analysis. Measured
 * over 60 pieces and 1448 nameable places, the root read off the keys alone
 * differs from the chord S0 announces at the same instant in **22.7%** of
 * cases — and in 77.5% of those the hand is holding notes that are not even
 * in the announced chord (one foreign note in 144, two in 93, all three in
 * 18).
 *
 * So the root is a claim this file cannot make on its own, and a panel that
 * says "un accord de Do mineur" one screen after S0 said `Eb6` is the
 * contradiction the whole journey is written against. It is stated only on
 * confirmation, never on the absence of a contradiction: no chord at that
 * instant means no confirmation, and the copy falls back to the structure —
 * "un accord mineur" — which is read off the offsets and cannot be false.
 */
export function announcedRootAt(
  windows: readonly {
    readonly startTick: number;
    readonly endTick: number;
    readonly chord: { readonly rootPitchClass: number };
  }[],
  tick: number,
): number | null {
  for (const window of windows) {
    if (window.startTick <= tick && tick < window.endTick) {
      return window.chord.rootPitchClass;
    }
  }

  return null;
}

// ── The chord under the hand ──────────────────────────────────────

/**
 * The chord the analyzer states over the **whole** span of a place, or
 * `null`.
 *
 * Stricter than `announcedRootAt`, which answers for one instant. A grip is
 * an occupation over a span, and a hand that keeps its keys down across a
 * chord change is holding two different things in turn: naming one of them
 * would be true for part of the time the panel is on screen. Several windows
 * are accepted when they all state the same chord, which is the ordinary
 * case of a harmony held over two bars and cut in two by the analyzer.
 */
export function announcedChordOver<
  C extends { readonly rootPitchClass: number; readonly quality: string },
>(
  windows: readonly {
    readonly startTick: number;
    readonly endTick: number;
    readonly chord: C;
  }[],
  startTick: number,
  endTick: number,
): C | null {
  const over = windows.filter(
    (w) => w.startTick < endTick && startTick < w.endTick,
  );

  if (over.length === 0) {
    return null;
  }

  const [head] = over;
  const agreed = over.every(
    (w) =>
      w.chord.rootPitchClass === head.chord.rootPitchClass &&
      w.chord.quality === head.chord.quality,
  );

  if (!agreed) {
    return null;
  }

  return head.chord;
}

/**
 * What each key of the place is **inside the chord**: `la fondamentale et la
 * quinte`.
 *
 * The one harmonic sentence the grip step can carry. Everything else it says
 * is a property of the hand — a fifth is a fifth wherever it is taken — and
 * `shapeName` is right to keep the harmony out of the shape's own name: the
 * four fifths of *Libérée délivrée* are Dm, B♭, C and Gm, so a chord in the
 * headline would be false at three places out of four. But the *place* is
 * fixed, the analyzer states a chord over it, and the roll is drawing that
 * chord's name a few centimetres away. The panel staying silent about it was
 * the panel-versus-roll divergence the journey treats as a bug everywhere
 * else.
 *
 * What keeps it honest is the same rule as `announcedRootAt`, twice over.
 * The chord is never derived from the keys: read off the keys alone, the root
 * disagrees with the announced chord in 22.7% of nameable places. And a
 * single key outside the chord silences the line, because 77.5% of those
 * disagreements are a hand holding notes the chord does not contain.
 *
 * `null` too when the hand holds the chord whole: `placeName` already names
 * it in the headline, and repeating it underneath in other words reads as
 * two different claims.
 */
export function chordRoleLine(
  place: PhraseGrip,
  chord: {
    readonly rootPitchClass: number;
    /** Semitone offsets of the chord's tones. See `chordIntervals`. */
    readonly intervals: readonly number[];
    /** The chord in words, in the player's notation: `Ré mineur`. */
    readonly name: string;
  },
): string | null {
  if (place.midis.length === 0 || chord.intervals.length === 0) {
    return null;
  }

  const tones = new Set(chord.intervals);
  const degrees: string[] = [];

  for (const midi of place.midis) {
    const interval = (((midi - chord.rootPitchClass) % 12) + 12) % 12;

    if (!tones.has(interval)) {
      return null;
    }

    const degree = DEGREE_NAME[interval];

    if (degree === undefined) {
      return null;
    }

    if (!degrees.includes(degree)) {
      degrees.push(degree);
    }
  }

  if (degrees.length >= chord.intervals.length) {
    return null;
  }

  return `${cap(list(degrees))} de ${chord.name}, l’accord qui sonne ici.`;
}

/**
 * Semitone above the root, named. Altered fifths and both thirds fold onto
 * the plain word: the distinction between a diminished and a perfect fifth
 * is not what this line is for, and the two would have to be explained
 * before they could be told apart.
 */
const DEGREE_NAME: Record<number, string> = {
  0: 'la fondamentale',
  2: 'la seconde',
  3: 'la tierce',
  4: 'la tierce',
  5: 'la quarte',
  6: 'la quinte',
  7: 'la quinte',
  8: 'la quinte',
  9: 'la sixte',
  10: 'la septième',
  11: 'la septième',
};

/**
 * How a place is spelled out: the letters, then everything else.
 *
 * Split in two because they are read differently. `letters` is what the
 * player looks for on the keyboard and belongs in type large enough to be
 * caught out of the corner of an eye; `detail` is the subordinate clause
 * that says where and with what.
 *
 * The form it replaced interleaved all three — `Ré3 (5), Fa3 (3) et La3 (1)`
 * — so reading the shape meant filtering an octave number and a finger out
 * of every item before the three letters could be seen as a chord at all.
 */
export type PlaceSpelling = {
  /** `Ré-Fa-La` */
  readonly letters: string;
  /** `3e octave, doigté 5-3-1`, or just the octave when fingering is partial. */
  readonly detail: string;
};

export function placeSpelling(
  place: PhraseGrip,
  pitchClassLabel: (pitchClass: number) => string,
): PlaceSpelling {
  const letters = place.midis
    .map((m) => pitchClassLabel(((m % 12) + 12) % 12))
    .join('-');

  const parts = [octaveClause(place.midis)];
  const fingering = fingeringClause(place);

  if (fingering !== null) {
    parts.push(fingering);
  }

  return { letters, detail: parts.join(', ') };
}

/** MIDI octave number, the one the note labels already carry: `Ré3` → 3. */
function octaveOf(midi: number): number {
  return Math.floor(midi / 12) - 1;
}

function ordinal(n: number): string {
  return n === 1 ? '1re' : `${n}e`;
}

function octaveClause(midis: readonly number[]): string {
  const low = octaveOf(Math.min(...midis));
  const high = octaveOf(Math.max(...midis));

  if (low === high) {
    return `${ordinal(low)} octave`;
  }

  if (high === low + 1) {
    return `octaves ${low} et ${high}`;
  }

  return `octaves ${low} à ${high}`;
}

/**
 * `doigté 5-3-1`, or `null` when the analysis did not finger every key.
 *
 * A key taken by two fingers is written `3/5` inside the run, so the slash
 * separates fingers on one key and the dash separates keys — writing both
 * with a dash would turn `3-5` and `1` into three keys.
 */
function fingeringClause(place: PhraseGrip): string | null {
  const marks = place.midis.map((m) => fingersOn(place, m));

  if (marks.some((f) => f.length === 0)) {
    return null;
  }

  return `doigté ${marks.map((f) => f.join('/')).join('-')}`;
}

// ── Copy ──────────────────────────────────────────────────────────

const HAND_LABEL: Record<'left' | 'right', string> = {
  left: 'gauche',
  right: 'droite',
};

/**
 * Spelled out only up to five, which is all a hand has. Attack and place
 * counts are left as digits past that: they run into the dozens, and a
 * spelled-out "quatorze" reads as prose where a number reads as a
 * measurement.
 */
const COUNT_WORD = ['', 'une', 'deux', 'trois', 'quatre', 'cinq'];

function count(n: number): string {
  // Guarded on the word being non-empty, not on the index being in range:
  // `COUNT_WORD[0]` is `''`, and `??` does not fire on an empty string, so
  // the obvious form answers `''` for zero and the sentence loses its
  // number without anything failing.
  return COUNT_WORD[n] || String(n);
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Enumerates keys as `A, B et C`. */
export function list(items: readonly string[]): string {
  if (items.length <= 1) {
    return items[0] ?? '';
  }

  return `${items.slice(0, -1).join(', ')} et ${items[items.length - 1]}`;
}

/**
 * The key the hand is anchored on while the shape is walked: the thumb's.
 *
 * Not the key the piece plays first, which is what `openingKeys` gives and
 * what act 1 used to hunt for. The two answer different questions. The
 * musical opening says *where the passage begins*; the anchor says *where the
 * hand is*, and a five-finger position is felt from the thumb outwards. On
 * a right hand sitting on Do-Ré-Mi-Fa-Sol whose passage happens to start on
 * Mi, anchoring the act on Mi asks the player to hold the middle of their own
 * hand still while reaching around it on both sides, which is not a position
 * anyone has ever felt that way.
 *
 * Falls back to the lowest finger number in the grip when the thumb does not
 * play — the finger nearest to it, and the same reference for the same
 * reason. Returns one key: an anchor is a finger, not a chord.
 */
export function anchorKeys(place: PhraseGrip): readonly number[] {
  let best: GripFinger | null = null;

  for (const assignment of place.fingers) {
    if (best === null || assignment.finger < best.finger) {
      best = assignment;
    }
  }

  if (best === null) {
    return [];
  }

  return [best.midi];
}

/**
 * What the panel says, at two sizes.
 *
 * The panel used to stack a lead, a target and a tail at 13, 30 and 13 px,
 * and read as a paragraph with one word in bold. A hand about to move needs
 * one order, large enough to be caught from the corner of an eye while it
 * looks at the keys — `Index sur Mi4` — and a line under it. Everything else
 * is either drawn on the keyboard or said in small type below it.
 */
export type GripHeadline = {
  readonly hero: HeroText;
  readonly sub: string;
};

/**
 * The order, as runs of text, some of them set in the hand's colour.
 *
 * The coloured runs are what the hand acts on — the finger, the note, the
 * octave: « **Pouce** sur **Mi**, **4**ème octave ». Split here so the view
 * never parses back a sentence this file had just assembled.
 */
export type HeroText = readonly HeroPart[];

export type HeroPart = {
  readonly text: string;
  readonly accent: boolean;
  /**
   * The line ends after this run. The order is set in 34-38 px type in a
   * panel about 500 px wide, so it wraps; left to the browser it broke
   * inside « sur Si » and read as « Auriculaire sur / Si, 3ème octave ».
   * The break goes where the sentence pauses.
   */
  readonly breakAfter?: boolean;
};

function plain(text: string): HeroPart {
  return { text, accent: false };
}

function strong(text: string): HeroPart {
  return { text, accent: true };
}

function hero(...parts: readonly HeroPart[]): HeroText {
  return parts.filter((part) => part.text !== '');
}

/** `A, B et C`, over runs rather than strings. */
function listParts(items: readonly (readonly HeroPart[])[]): HeroPart[] {
  return items.flatMap((item, i) =>
    i === 0 ? [...item] : [plain(i === items.length - 1 ? ' et ' : ', '), ...item],
  );
}

/**
 * A finger as the subject of a short order: `Index`, `Pouce`.
 *
 * No article, unlike `fingerName`: « Index sur Mi » is how a teacher says it
 * with a hand already over the keys.
 */
const FINGER_TITLE: Record<number, string> = {
  1: 'Pouce',
  2: 'Index',
  3: 'Majeur',
  4: 'Annulaire',
  5: 'Auriculaire',
};

/** A key to name, with the fingers the analysis puts on it. */
export type FingeredKey = {
  readonly midi: number;
  readonly fingers: readonly number[];
};

/** Coloured only when it names a finger: « Deux doigts » names none. */
function fingerTitle(keys: readonly FingeredKey[]): HeroPart {
  if (keys.length > 1) {
    return plain(`${cap(count(keys.length))} doigts`);
  }

  const fingers = keys[0]?.fingers ?? [];
  const title = fingers.length === 1 ? FINGER_TITLE[fingers[0]] : undefined;

  return title === undefined ? plain('Un doigt') : strong(title);
}

/**
 * « **Auriculaire** sur **Si**, **3**ème octave »: the finger, then the key.
 *
 * Shared by the two screens that ask for a first key — a shape's and an
 * orientation's — so the same gesture is always asked for in the same words.
 */
export function fingerOnKeys(
  keys: readonly FingeredKey[],
  noteLabel: (midi: number) => string,
): HeroText {
  return hero(
    fingerTitle(keys),
    plain(' sur '),
    ...keysInWords(
      keys.map((key) => key.midi),
      noteLabel,
    ),
  );
}

function fingeredOn(
  place: PhraseGrip,
  midis: readonly number[],
): FingeredKey[] {
  return midis.map((midi) => ({ midi, fingers: fingersOn(place, midi) }));
}

/**
 * The keys an order names, with the octave said in words: « **Mi**,
 * **4**ème octave ».
 *
 * Never `Mi4`: a digit stuck to a note name reads as a fingering everywhere
 * else in this product — see `splitOctave`. The octave is said once when the
 * keys share it; otherwise each key says its own.
 */
function keysInWords(
  midis: readonly number[],
  noteLabel: (midi: number) => string,
): HeroPart[] {
  const keys = midis.map((midi) => {
    const label = noteLabel(midi);
    const parts = splitOctave(label);

    return parts === null ? { name: label, octave: null } : parts;
  });
  const octaves = new Set(keys.map((k) => k.octave));
  const [shared] = octaves;

  if (octaves.size === 1 && shared !== null && shared !== undefined) {
    return [
      ...listParts(keys.map((k) => [strong(k.name)])),
      { text: ',', accent: false, breakAfter: true },
      ...octaveParts(shared),
    ];
  }

  return listParts(
    keys.map((k) =>
      k.octave === null
        ? [strong(k.name)]
        : [strong(k.name), plain(' ('), ...octaveParts(k.octave), plain(')')],
    ),
  );
}

/** « **4**ème octave »: the number is what the eye looks for. */
function octaveParts(octave: string): HeroPart[] {
  return [strong(octave), plain(`${octave === '1' ? 'ère' : 'ème'} octave`)];
}

/** Act 1: the key the hand rests on, and the finger on it. */
export function huntHeadline(
  place: PhraseGrip,
  anchor: readonly number[],
  noteLabel: (midi: number) => string,
): GripHeadline {
  return {
    hero: fingerOnKeys(fingeredOn(place, anchor), noteLabel),
    sub:
      anchor.length > 1
        ? 'Appuie, et garde les doigts dessus.'
        : 'Appuie, et garde le doigt dessus.',
  };
}

/**
 * Act 2: the rest of the shape, once the anchor is found.
 *
 * The keys to add are left to the drawing, which shows them on the keys with
 * their fingers. The line under it counts down what is left and names what
 * is taken, so a key found stays found on screen as it does in the
 * evaluation.
 */
export function walkHeadline(
  place: PhraseGrip,
  anchor: readonly number[],
  covered: ReadonlySet<number>,
  noteLabel: (midi: number) => string,
): GripHeadline {
  const rest = place.midis.filter((m) => !anchor.includes(m));
  const found = rest.filter((m) => covered.has(m));

  return {
    hero: hero(
      plain(rest.length > 1 ? 'Touche les autres notes' : "Touche l'autre note"),
    ),
    sub: walkSub(rest.length, found, noteLabel),
  };
}

function walkSub(
  total: number,
  found: readonly number[],
  noteLabel: (midi: number) => string,
): string {
  if (found.length === 0) {
    return total > 1
      ? "Une à une, dans l'ordre que tu veux."
      : 'Elle est allumée sur le clavier.';
  }

  const left = total - found.length;
  const taken =
    found.length > 1
      ? `${list(found.map(noteLabel))} sont acquis.`
      : `${noteLabel(found[0])} est acquis.`;

  return `Encore ${count(left)} note${left > 1 ? 's' : ''}. ${taken}`;
}

/**
 * A place the piece strikes whole: one act, and its letters are the order.
 *
 * The name and the fingering go underneath. They are true, and they are not
 * what the hand needs to read first.
 */
export function wholeHeadline(
  place: PhraseGrip,
  pitchClassLabel: (pitchClass: number) => string,
  announcedRoot: number | null,
): GripHeadline {
  const { letters, detail } = placeSpelling(place, pitchClassLabel);
  const name = placeName(place, pitchClassLabel, announcedRoot);

  return {
    hero:
      place.midis.length > 1
        ? hero(plain('Prends '), strong(letters), plain(' ensemble'))
        : hero(plain('Prends '), strong(letters)),
    sub: `${cap(name === null ? detail : `${name}, ${detail}`)}.`,
  };
}

/**
 * A place after the first: the same shape, somewhere else.
 *
 * Deliberately not an announcement. The player is not being given a new
 * thing — that is why the shape was deduplicated — so the order is the move,
 * and which place out of how many is left to the header.
 */
export function nextPlaceHeadline(
  shape: HandShape,
  place: PhraseGrip,
  placeIndex: number,
  pitchClassLabel: (pitchClass: number) => string,
): GripHeadline {
  const previous = shape.occurrences[placeIndex - 1];
  const lower = Math.min(...place.midis) < Math.min(...previous.midis);
  const { letters, detail } = placeSpelling(place, pitchClassLabel);

  return {
    hero: hero(plain('Même prise, '), strong(lower ? 'plus bas' : 'plus haut')),
    sub: `${letters}, ${detail}.`,
  };
}

/** The beat after the shape is taken, before the step moves on. */
export function takenHeadline(sub: string): GripHeadline {
  return { hero: hero(strong("C'est ça.")), sub };
}

/**
 * The orientation screen's act: where the passage starts.
 *
 * The same order as a shape's first key — the finger, then the key — since
 * it is the same gesture. What differs is underneath: the hand does not
 * settle here, and the key it names is where the passage begins.
 */
export function orientationHuntHeadline(
  place: PhraseGrip,
  anchor: readonly number[],
  noteLabel: (midi: number) => string,
): GripHeadline {
  return {
    hero: fingerOnKeys(fingeredOn(place, anchor), noteLabel),
    sub: "C'est là que le passage commence.",
  };
}

/**
 * The orientation screen: no shape to hold, so how far the hand ranges.
 *
 * Never false — it asserts no simultaneity, only a band of pitch — and it
 * answers the question a hand about to play actually has: *am I going to have
 * to move?*
 */
export function orientationHeadline(
  hand: 'left' | 'right',
  lowMidi: number,
  highMidi: number,
  noteLabel: (midi: number) => string,
): GripHeadline {
  return {
    hero: hero(plain('Pas de forme à tenir')),
    sub: `Ta main ${HAND_LABEL[hand]} se déplace beaucoup ici : elle va de ${noteLabel(lowMidi)} à ${noteLabel(highMidi)}.`,
  };
}

/**
 * What is said under « C'est ça » once the last place of the last shape is
 * taken.
 */
export function gripSuccess(
  hand: 'left' | 'right',
  shape: HandShape,
  shapeCount: number,
): string {
  const places = shape.asked;

  if (places > 1) {
    return `La même prise à ${count(places)} endroits.`;
  }

  if (shapeCount === 1) {
    return `Ta main ${HAND_LABEL[hand]} ne prend que cette forme ici.`;
  }

  return `${cap(count(shapeCount))} formes, et c'est tout ce que fait ta main ${HAND_LABEL[hand]} ici.`;
}

/** The beat between two places of one shape: encouragement, not a lesson. */
export function placeTakenLine(): string {
  return 'Refais-la ailleurs.';
}

/** Announces the next shape, on the pause between two of them. */
export function nextShapeLine(): string {
  return 'Une autre forme maintenant.';
}

/**
 * Where else the phrase uses this configuration, or `null` when it uses it
 * once.
 *
 * The direction is derived, never asserted: on the passage this step was
 * built against the four places go down, up, then down, and a copy claiming
 * a descent would be describing a different piece.
 */
export function recurrenceLine(shape: HandShape): string | null {
  const here = shape.occurrences.length;
  const inPiece = shape.occurrences[0].shapeCountInPiece;

  // The piece-wide count first when it is the bigger story. Learning one
  // configuration that comes back eleven times is an investment, and saying
  // so is one of the few places this journey can show an adult a return on
  // the effort rather than a chore.
  if (inPiece >= PAYOFF_RETURNS) {
    return `Cette prise revient ${inPiece} fois dans le morceau : tu l'apprends une fois, elle te sert partout.`;
  }

  if (here < 2) {
    return null;
  }

  const tail =
    shape.asked < here ? `, on en prend ${count(shape.asked)}` : directionTail(shape);

  return `La même prise revient ${count(here)} fois dans la phrase${tail}.`;
}

/**
 * Returns past which the piece-wide count is worth leading with.
 *
 * Below it the number is unremarkable and the sentence would read as
 * padding; above, it is the most encouraging true thing on the screen.
 */
const PAYOFF_RETURNS = 8;

function directionTail(shape: HandShape): string {
  switch (shapeDirection(shape)) {
    case 'down':
      return ', de plus en plus grave';
    case 'up':
      return ', de plus en plus aigu';
    default:
      return '';
  }
}

/**
 * Feedback while the player hunts for the opening key. Naming the direction
 * is the whole help: it keeps the search a search instead of turning it into
 * "the key that is lit".
 */
export function openingNudge(
  opening: readonly number[],
  active: ReadonlySet<number>,
  noteLabel: (midi: number) => string,
): string | null {
  const target = opening[0];
  const pressed = [...active];

  if (target === undefined || pressed.length === 0) {
    return null;
  }

  const closest = pressed.reduce((best, midi) =>
    Math.abs(midi - target) < Math.abs(best - target) ? midi : best,
  );

  if (opening.includes(closest)) {
    return null;
  }

  return `Ce n'est pas ${noteLabel(target)}. Cherche plus ${closest < target ? 'haut' : 'bas'}.`;
}

/**
 * The line that names a stretch the hand cannot hold, or `null`.
 *
 * Four rules, and each one is a thing not to say.
 *
 * It talks about **the notes**, never about the player's hand: their span is
 * unknown, and some adults reach a tenth. It prescribes **no technique** —
 * roll it, pedal it — because nothing here can verify that the advice fits,
 * and a wrong technical instruction costs more than silence. And it only
 * appears past a **frank** margin, so that something merely difficult is
 * never announced as impossible.
 *
 * The fourth is why it names its keys. **It is never about the shape on
 * screen**: a wide gap is by construction a stretch that is *not* a shape
 * (`buildHandShapePlan` sorts the hand's placements into one or the other),
 * and the two are different moments of the unit, since placement ranges
 * never overlap. Read as « ces notes », under a panel showing an octave the
 * step then asks to hold, the sentence said the exact opposite of what the
 * screen was asking for. So it says where else it means, and names the two
 * keys it is about.
 *
 * Said rather than swallowed because it hides something *harder* than what
 * the panel shows. Naming it moves what the player concludes when they hit
 * it: "this passage is hard" instead of "I am bad".
 */
export function wideGapLine(
  gaps: readonly WideGap[],
  noteLabel: (midi: number) => string,
): string | null {
  const [widest] = namedGaps(gaps);

  if (widest === undefined) {
    return null;
  }

  const where =
    widest.startMeasure === widest.endMeasure
      ? `mesure ${widest.startMeasure}`
      : `mesures ${widest.startMeasure} à ${widest.endMeasure}`;

  return `Ailleurs dans le passage, ${where} : les notes vont de ${noteLabel(
    widest.lowMidi,
  )} à ${noteLabel(
    widest.highMidi,
  )}, trop éloignées pour être tenues ensemble. Ta main devra se déplacer.`;
}

/** Message for the state the hand is currently in, or `null` when silent. */
export function gripStatusMessage(
  evaluation: GripEvaluation,
  noteLabel: (midi: number) => string,
): string | null {
  if (evaluation.phase === 'wrong-octave') {
    const offset = evaluation.octaveOffset ?? 0;
    const dir = offset < 0 ? 'bas' : 'haut';
    const amount = Math.abs(offset) === 12 ? 'une octave' : 'deux octaves';

    return `C'est la bonne forme, ${amount} trop ${dir}.`;
  }

  if (evaluation.phase === 'extra') {
    const [first] = evaluation.extra;

    return `${noteLabel(first)} n'est pas dans la prise.`;
  }

  return null;
}

/**
 * The nudge shown once the hand has sat on an incomplete shape.
 *
 * It names the missing key **and its finger**, which is now a fact about the
 * grip rather than a guess: inside one grip the assignment does not change.
 */
export function gripNudge(
  place: PhraseGrip,
  evaluation: GripEvaluation,
  noteLabel: (midi: number) => string,
): string | null {
  if (evaluation.phase !== 'partial') {
    return null;
  }

  const [first] = evaluation.missing;

  if (first === undefined) {
    return null;
  }

  return `Il manque ${keyWithFinger(place, first, noteLabel)}.`;
}
